import React, { useState, useRef, useEffect } from 'react';
import {
  Paper, TextField, Stack, Button, Typography, IconButton, Box,
  Snackbar, Alert, Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from '../../utils/axios';

export default function BulkShortenForm() {
  const [inputs, setInputs] = useState([{ url: '', name: '' }]);
  const [results, setResults] = useState([]);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const lastInputRef = useRef(null);

  const handleChange = (idx, field, val) => {
    const clone = [...inputs];
    clone[idx][field] = val;
    setInputs(clone);
  };

  const handleAdd = () => setInputs(prev => [...prev, { url: '', name: '' }]);
  const handleRemove = (idx) => {
    if (inputs.length === 1) return;
    const clone = [...inputs];
    clone.splice(idx, 1);
    setInputs(clone);
  };

  useEffect(() => {
    if (lastInputRef.current) lastInputRef.current.focus();
  }, [inputs.length]);

  const handleSubmit = () => {
    const valid = inputs.filter(i => i.url.trim() !== '');
    if (!valid.length) {
      setSnack({ open: true, msg: 'Cần ít nhất 1 URL hợp lệ', severity: 'error' });
      return;
    }
    axios.post('/links/bulk', { links: valid })
      .then(res => {
        setResults(res.data.links || []);
        setSnack({ open: true, msg: 'Rút gọn thành công!', severity: 'success' });
        setInputs([{ url: '', name: '' }]);
      })
      .catch(() => {
        setSnack({ open: true, msg: 'Có lỗi xảy ra khi rút gọn!', severity: 'error' });
      });
  };

const handleCopy = () => {
  const text = results.map(r => {
    const shortUrl = r.short_url || `${window.location.origin}/${r.short_code}`;
    return `${r.link_name || '(Không tên)'}: ${shortUrl}`;
  }).join('\n');

  // Kiểm tra có hỗ trợ clipboard không
  if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    navigator.clipboard.writeText(text).then(() => {
      setSnack({ open: true, msg: 'Đã sao chép danh sách link!', severity: 'success' });
    }).catch(() => {
      setSnack({ open: true, msg: 'Sao chép thất bại!', severity: 'error' });
    });
  } else {
    // Fallback cho trình duyệt cũ
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setSnack({ open: true, msg: 'Đã sao chép (compatibility mode)!', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, msg: 'Sao chép thất bại!', severity: 'error' });
    }
  }
};


  return (
    <Paper sx={{ p: 3, borderRadius: 3, mt: 4 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        <span role="img" aria-label="multi-link" style={{ marginRight: 8 }}>🔗</span>
        Rút gọn nhiều link cùng lúc
      </Typography>

      <Stack spacing={2}>
        {inputs.map((item, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="URL gốc"
              inputRef={idx === inputs.length - 1 ? lastInputRef : null}
              value={item.url}
              onChange={e => handleChange(idx, 'url', e.target.value)}
              sx={{ flex: '0 1 60%', minWidth: 180 }}
              size="small"
              autoComplete="off"
            />
            <TextField
              label="Tên (tuỳ chọn)"
              value={item.name}
              onChange={e => handleChange(idx, 'name', e.target.value)}
              sx={{ flex: '0 1 35%', minWidth: 120 }}
              size="small"
              autoComplete="off"
            />
            <IconButton onClick={() => handleRemove(idx)} sx={{ flex: '0 1 5%', ml: 0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={handleAdd} sx={{ borderRadius: 2 }}>+ Thêm dòng</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ borderRadius: 2 }}>Rút gọn</Button>
        </Stack>
      </Stack>

      {/* BẢNG KẾT QUẢ */}
      {results.length > 0 && (
        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography fontWeight={700}>🎉 Kết quả rút gọn</Typography>
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              variant="outlined"
            >
              Sao chép tất cả
            </Button>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Link rút gọn</TableCell>
                <TableCell>Tên hiển thị</TableCell>
                <TableCell>Trạng thái</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <a href={r.short_url || `/r/${r.short_code}`} target="_blank" rel="noopener noreferrer">
                      {r.short_url || `${window.location.origin}/${r.short_code}`}
                    </a>
                  </TableCell>
                  <TableCell>{r.link_name || '—'}</TableCell>
                  <TableCell>{r.existed ? 'Đã tồn tại' : 'Mới tạo'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.msg}</Alert>
      </Snackbar>
    </Paper>
  );
}
